import { useMemo } from "react";
import "./Calendar.css";
import CalendarItem from "./CalendarItem";

const fullDayHeaders = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const weekdayHeaders = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

function getStartOfWeek(date, showWeekends = true) {
  let day = (date.getDay() + 6) % 7;

  if (!showWeekends) {
    if (day >= 5) {
      day = 0;
    }
  }

  const start = new Date(date);
  start.setDate(date.getDate() - day);
  start.setHours(0, 0, 0, 0);
  return start;
}

function formatDateKey(date) {
  return date.toLocaleDateString("sv-SE");
}

function generateWeekGrid(date, showWeekends = true) {
  const startOfWeek = getStartOfWeek(date, showWeekends);
  const weekDays = [];

  const daysCount = showWeekends ? 7 : 5;

  for (let i = 0; i < daysCount; i++) {
    const day = new Date(startOfWeek);

    if (showWeekends) {
      day.setDate(startOfWeek.getDate() + i);
      weekDays.push(day);
    } else {
      day.setDate(startOfWeek.getDate() + i);
      weekDays.push(day);
    }
  }

  return weekDays;
}

export default function WeeklyGrid({
  date = new Date(),
  showWeekends = true,
  onSelectDay,
  data = [],
}) {
  const dayHeaders = showWeekends ? fullDayHeaders : weekdayHeaders;

  const weekDays = generateWeekGrid(date, showWeekends);

  const dataByDate = useMemo(() => {
    const map = new Map();

    if (!Array.isArray(data)) return map;

    data.forEach((entry) => {
      const key = formatDateKey(new Date(entry.scheduleDate));
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(entry);
    });

    for (const [key, entries] of map) {
      entries.sort((a, b) => {
        const [aH, aM] = a.startTime ? a.startTime.split(":").map(Number) : [0, 0];
        const [bH, bM] = b.startTime ? b.startTime.split(":").map(Number) : [0, 0];
        return aH * 60 + aM - (bH * 60 + bM);
      });
    }

    return map;
  }, [data]);

  const onDayClick = (day) => {
    if (day && onSelectDay) {
      onSelectDay(day);
    }
  };

  return (
    <div className="weekly-grid">
      <div
        className="weekly-grid-header"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${dayHeaders.length}, 1fr)`,
        }}
      >
        {dayHeaders.map((day) => (
          <div key={day} className="weekly-grid-header-cell">
            {day}
          </div>
        ))}
      </div>

      <div
        className="weekly-grid-body"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${dayHeaders.length}, 1fr)`,
        }}
      >
        {weekDays.map((day, idx) => {
          const today = new Date();
          const isToday =
            day.getDate() === today.getDate() &&
            day.getMonth() === today.getMonth() &&
            day.getFullYear() === today.getFullYear();

          const dayKey = formatDateKey(day);
          const dayEntries = dataByDate.get(dayKey) || [];

          const displayEntries = dayEntries.slice(0, 3);
          const remainingCount = dayEntries.length - 3;

          return (
            <div
              key={idx}
              className="weekly-grid-cell"
              onClick={() => onDayClick(day)}
            >
              <div
                className={`monthly-grid-cell-date ${
                  isToday ? "monthly-grid-cell-date-today" : ""
                }`}
              >
                {day.getDate()}
              </div>

              <div className="monthly-grid-cell-entries">
                {displayEntries.map((entry, i) => (
                  <CalendarItem
                    key={entry._id}
                    data={entry}
                    compact={false}
                    remainingCount={
                      i === 2 && remainingCount > 0 ? remainingCount : 0
                    }
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
